import { useState, useEffect, useMemo } from "react";
import { api, useTenant } from "../App";
import { getApiPrefix } from '../utils/apiHelper';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar as CalIcon, ChevronLeft, ChevronRight, Clock, AlertTriangle } from "lucide-react";
import { PageLoader } from "../components/ui/page-loader";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../contexts/I18nContext";

const MONTH_NAMES = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const DAY_NAMES = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
const fmt = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

export default function CalendarioScadenzePage() {
  const { t } = useTranslation();
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const navigate = useNavigate();
  const { tenantSlug } = useTenant() || {};

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Fetch from multiple sources: scadenziario (invoices + leasing + etc) + scheduled payments
        const [scadRes, spRes] = await Promise.all([
          api.get(`${getApiPrefix()}/reports/scadenziario`).catch(() => ({ data: { items: [] } })),
          api.get(`${getApiPrefix()}/scheduled-payments`).catch(() => ({ data: [] })),
        ]);
        
        // Merge all deadlines
        const scadItems = Array.isArray(scadRes.data) ? scadRes.data : (scadRes.data?.items || []);
        const spItems = Array.isArray(spRes.data) ? spRes.data : (spRes.data?.items || []);
        
        // Normalize scheduled payments to have data_scadenza field
        const normalizedSP = spItems.map(sp => ({
          ...sp,
          data_scadenza: sp.data_scadenza || sp.data,
          descrizione: sp.descrizione || 'Scadenza programmata',
          importo: Math.abs(sp.importo || 0),
        }));

        // Also fetch unpaid invoices as virtual deadlines
        let invoiceDeadlines = [];
        try {
          const invRes = await api.get(`${getApiPrefix()}/invoices?stato_pagamento=non_pagata&page_size=500`);
          const invItems = invRes.data?.items || (Array.isArray(invRes.data) ? invRes.data : []);
          invoiceDeadlines = invItems
            .filter(inv => inv.data && (inv.stato_pagamento !== 'pagata'))
            .map(inv => ({
              data_scadenza: inv.data,
              descrizione: `${inv.tipo_documento} ${inv.numero || ''} - ${(inv.fornitore_cliente || '').substring(0, 25)}`,
              importo: Math.abs(inv.totale || 0),
              stato: inv.stato_pagamento === 'pagata' ? 'pagata' : 'non_pagata',
              tipo: inv.tipo_documento,
            }));
        } catch {}
        
        setDeadlines([...scadItems, ...normalizedSP, ...invoiceDeadlines]);
      } catch {
        setDeadlines([]);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7;

  const deadlinesByDate = useMemo(() => {
    const map = {};
    for (const d of deadlines) {
      const dateStr = d.data_scadenza || d.data;
      if (!dateStr) continue;
      let key;
      if (dateStr.includes('-') && dateStr.split('-')[0].length === 4) {
        key = dateStr.substring(0, 10);
      } else {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          key = `${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
        } else continue;
      }
      if (!map[key]) map[key] = [];
      map[key].push(d);
    }
    return map;
  }, [deadlines]);

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  if (loading) return <PageLoader variant="section" text="Caricamento calendario..." />;

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <CalIcon className="h-6 w-6" />Calendario Scadenze
        </h1>
      </div>

      {/* Navigation */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={prevMonth}><ChevronLeft className="h-5 w-5" /></Button>
            <h2 className="text-lg font-semibold">{MONTH_NAMES[month]} {year}</h2>
            <Button variant="ghost" onClick={nextMonth}><ChevronRight className="h-5 w-5" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-7 gap-px bg-muted rounded-lg overflow-hidden">
            {/* Day headers */}
            {DAY_NAMES.map(d => (
              <div key={d} className="bg-background p-2 text-center text-xs font-semibold text-muted-foreground">{d}</div>
            ))}
            
            {/* Empty cells for days before month start */}
            {Array.from({ length: firstDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} className="bg-background min-h-[100px] p-2" />
            ))}

            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateKey = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const dayDeadlines = deadlinesByDate[dateKey] || [];
              const isToday = dateKey === todayKey;
              const isPast = new Date(year, month, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
              const hasOverdue = isPast && dayDeadlines.some(d => d.stato !== 'pagata');

              return (
                <div key={day} className={`bg-background min-h-[100px] p-2 border-b border-r transition-colors hover:bg-muted/30 ${isToday ? 'ring-2 ring-blue-400 ring-inset' : ''}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${isToday ? 'bg-blue-500 text-white h-6 w-6 rounded-full flex items-center justify-center' : ''}`}>{day}</span>
                    {dayDeadlines.length > 0 && (
                      <Badge variant="outline" className={`text-xs px-1 ${hasOverdue ? 'border-red-300 text-red-600' : 'border-blue-300 text-blue-600'}`}>
                        {dayDeadlines.length}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {dayDeadlines.slice(0, 3).map((dl, j) => {
                      const paid = dl.stato === 'pagata';
                      return (
                        <div key={j} className={`text-xs px-1.5 py-0.5 rounded truncate ${paid ? 'bg-emerald-100 text-emerald-700 line-through' : hasOverdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {fmt(dl.importo)} {dl.descrizione ? `- ${dl.descrizione.substring(0, 15)}` : ''}
                        </div>
                      );
                    })}
                    {dayDeadlines.length > 3 && <p className="text-xs text-muted-foreground">+{dayDeadlines.length - 3} altre</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1"><div className="h-3 w-3 rounded bg-amber-100 border border-amber-300" />{t('common.expiringSoon')}</div>
        <div className="flex items-center gap-1"><div className="h-3 w-3 rounded bg-red-100 border border-red-300" />{t('common.expiredPlural')}</div>
        <div className="flex items-center gap-1"><div className="h-3 w-3 rounded bg-emerald-100 border border-emerald-300" />{t('invoices.paidPlural')}</div>
        <div className="flex items-center gap-1"><div className="h-3 w-3 rounded-full ring-2 ring-blue-400 bg-white" />{t('common.today')}</div>
      </div>
    </div>
  );
}
